import { useEffect, useMemo, useState } from 'react';
import { api, type MigrationCaseDto } from '@/lib/api';
import { masterdataApi } from '@/lib/courses';

export type TeacherArtRatesPerson = {
  teacher?: {
    artRates?: Array<{
      unterrichtsform_art_id?: number | string;
      art_id?: number | string;
      rate?: number | string;
    }>;
  } | null;
  artRates?: Array<{
    unterrichtsform_art_id?: number | string;
    art_id?: number | string;
    rate?: number | string;
  }>;
};

export type MigrationOfferPreviewData = {
  teacherName: string;
  teacherWpUserId: number;
  unterrichtsformId: number;
  durationMin: number;
};

export function extractMigrationOfferPreviewData(caseItem: MigrationCaseDto | null): MigrationOfferPreviewData | null {
  if (!caseItem) return null;
  const snapshot = (caseItem.snapshot ?? {}) as Record<string, unknown>;
  const office = (snapshot.office_contract_adjustment ?? {}) as {
    payload?: {
      lesson?: Record<string, unknown>;
    };
  };
  const lesson = (office.payload?.lesson ?? {}) as Record<string, unknown>;
  return {
    teacherName: String(lesson.teacher_name ?? snapshot.legacy_teacher_name ?? 'Lehrer'),
    teacherWpUserId: Number(lesson.teacher_wp_user_id ?? 0),
    unterrichtsformId: Number(lesson.unterrichtsform_id ?? 0),
    durationMin: Number(lesson.duration_min ?? snapshot.legacy_duration_min ?? 0),
  };
}

export function computeTeacherHonorarPerLessonCents(
  teacherPerson: TeacherArtRatesPerson | null,
  artId: number | null | undefined,
  durationMin: number,
): number | null {
  if (!teacherPerson || !artId || durationMin <= 0) return null;
  const candidates = Array.isArray(teacherPerson.teacher?.artRates)
    ? teacherPerson.teacher?.artRates || []
    : (Array.isArray(teacherPerson.artRates) ? teacherPerson.artRates : []);
  const matched = candidates.find((entry) => {
    const candidateArtId = Number(entry?.unterrichtsform_art_id ?? entry?.art_id ?? 0);
    return candidateArtId === artId;
  });
  if (!matched) return null;
  const ratePerHourEuro = Number(matched.rate ?? 0);
  if (!Number.isFinite(ratePerHourEuro) || ratePerHourEuro <= 0) return null;
  const ratePerHourCents = Math.round(ratePerHourEuro * 100);
  return Math.round(ratePerHourCents * (durationMin / 60));
}

export function useMigrationOfferHonorarMissing(caseItem: MigrationCaseDto, enabled: boolean): {
  loading: boolean;
  missing: boolean;
  message: string;
} {
  const previewData = useMemo(() => extractMigrationOfferPreviewData(caseItem), [caseItem]);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!enabled || !previewData) {
      setLoading(false);
      setMissing(false);
      return;
    }
    const { teacherWpUserId, unterrichtsformId, durationMin } = previewData;
    if (teacherWpUserId <= 0 || unterrichtsformId <= 0 || durationMin <= 0) {
      setLoading(false);
      setMissing(true);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [person, forms] = await Promise.all([
          api.get<TeacherArtRatesPerson>(`/api/personen/${teacherWpUserId}`).catch(() => null),
          masterdataApi.unterrichtsformen().catch(() => []),
        ]);
        if (cancelled) return;
        const form = (forms || []).find((f) => Number(f.id) === unterrichtsformId);
        const artId = Number(form?.art_id ?? 0);
        const honorar = computeTeacherHonorarPerLessonCents(person, artId, durationMin);
        setMissing(honorar === null || honorar <= 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, previewData]);

  const message = missing
    ? `Angebot kann nicht gesendet werden: Bei ${previewData?.teacherName || 'Lehrer'} ist kein Honorar hinterlegt. Bitte im Lehrerprofil unter Honorare pflegen.`
    : '';
  return { loading, missing, message };
}
